import React, { useState } from 'react';
import { styled, keyframes } from '@mui/system';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

interface AlertPopupProps {
  open: boolean;
  title?: string;
  message: string;
  onClose: () => void;
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
  showConfirmButton?: boolean; // New prop
}

// Shake animation keyframes
const shakeAnimation = keyframes`
  0% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  50% { transform: translateX(10px); }
  75% { transform: translateX(-10px); }
  100% { transform: translateX(0); }
`;
const rotationalShakeAnimation = keyframes`
  0% { transform: rotate(0deg) translateX(0) translateY(0); }
  20% { transform: rotate(-5deg) translateY(-2px); }
  40% { transform: rotate(5deg) translateX(-10px); translateY(2px); }
  60% { transform: rotate(-3deg)  translateX(10px) translateY(-1px); }
  80% { transform: rotate(3deg) translateX(-10px) translateY(1px); }
  100% { transform: rotate(0deg) translateX(0) translateY(0); }
`;

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    width: '400px', // Fixed width
    maxWidth: '90%', // Responsive fallback for smaller screens
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    // boxShadow: theme.shadows[5],
    minHeight: '150px', // Ensure consistent minimum height
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    animation: 'none', // Initial state, no animation
  },
  '&.shake': {
    '& .MuiPaper-root': {
      animation: `${shakeAnimation} 0.4s ease-in-out`,
    },
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
//   fontSize: theme.typography.h6.fontSize,
//   fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
  textAlign: 'center',
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2),
  '& .MuiTypography-root': {
    // fontSize: theme.typography.body1.fontSize,
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(1),
  justifyContent: 'center',
  gap: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 3),
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
//   fontWeight: theme.typography.fontWeightMedium,
//   fontSize: theme.typography.button.fontSize,
}));

const AlertPopup: React.FC<AlertPopupProps> = ({
  open,
  title = 'Alert',
  message,
  onClose,
  onConfirm,
  confirmText = 'OK',
  cancelText = 'Cancel',
  showConfirmButton = true, // New prop with default true
}) => {
  const [shake, setShake] = useState(false);

  const handleClose = (event: object, reason: string) => {
    if (reason === 'backdropClick') {
      setShake(true);
      setTimeout(() => setShake(false), 400);
    } else {
      onClose();
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      className={shake ? 'shake' : ''}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      {title && <StyledDialogTitle id="alert-dialog-title">{title}</StyledDialogTitle>}
      <StyledDialogContent>
        <Typography id="alert-dialog-description">{message}</Typography>
      </StyledDialogContent>
      <StyledDialogActions>
        {onConfirm && showConfirmButton && (
          <StyledButton onClick={onConfirm} color="error" variant="contained">
            {confirmText}
          </StyledButton>
        )}
        <StyledButton onClick={onClose} color="secondary" variant="outlined">
          {cancelText}
        </StyledButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default AlertPopup;
